import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Download, Upload, ArrowLeftRight, TrendingUp, Coins } from 'lucide-react';
import { AllocationChart } from './AllocationChart';
import { totalPortfolioValue, spotBalance, spotUpnl, marginBalance, marginPositionsUpnl, marginUsedMargin, availableMargin, currentLeverage, mainAccountValue, futuresPositions, futuresUnrealizedPnl, futuresBalance, futuresAccountValue, earnBalance as importedEarnBalance, earnRewards as importedEarnRewards } from '../../utils/portfolioData';
// Override earn balance and rewards
const earnBalance = 3247.89;
const earnRewards = 42.15; // Weekly rewards
// Calculate futures leverage from imported data
const futuresPositionValue = futuresPositions.reduce((sum, pos) => sum + pos.size * pos.markPrice, 0);
const futuresLeverage = futuresBalance > 0 ? futuresPositionValue / futuresBalance : 0;
// Mock data for isolated and cross margin
const isolatedMarginBalance = 3200.0;
const isolatedMarginUsed = 1280.0;
const isolatedMarginUpnl = 641.0;
const isolatedMarginPositions = 1;
const isolatedLeverage = isolatedMarginBalance > 0 ? isolatedMarginUsed / isolatedMarginBalance : 0;
const isolatedMarginHealth = isolatedMarginBalance > 0 ? (isolatedMarginBalance - isolatedMarginUsed) / isolatedMarginBalance * 100 : 0;
const crossMarginBalance = 1800.0;
const crossMarginUsed = 540.0;
const crossMarginUpnl = 157.5;
const crossMarginPositions = 1;
const crossLeverage = crossMarginBalance > 0 ? crossMarginUsed / crossMarginBalance : 0;
const crossMarginHealth = crossMarginBalance > 0 ? (crossMarginBalance - crossMarginUsed) / crossMarginBalance * 100 : 0;
// Spot 24h change calculation (mock data)
const spotChange24h = 1250.45; // Same as spotUpnl for now
const spotChange24hPercent = spotChange24h / spotBalance * 100;
// Data for different time ranges - Smoother, less volatile
const chartData = {
  '24H': [{
    time: '00:00',
    value: 48500.23,
    timestamp: '2025-01-16 00:00'
  }, {
    time: '01:00',
    value: 48623.45,
    timestamp: '2025-01-16 01:00'
  }, {
    time: '02:00',
    value: 48534.67,
    timestamp: '2025-01-16 02:00'
  }, {
    time: '03:00',
    value: 48678.89,
    timestamp: '2025-01-16 03:00'
  }, {
    time: '04:00',
    value: 48823.12,
    timestamp: '2025-01-16 04:00'
  }, {
    time: '05:00',
    value: 48912.34,
    timestamp: '2025-01-16 05:00'
  }, {
    time: '06:00',
    value: 49034.56,
    timestamp: '2025-01-16 06:00'
  }, {
    time: '07:00',
    value: 49123.78,
    timestamp: '2025-01-16 07:00'
  }, {
    time: '08:00',
    value: 49078.9,
    timestamp: '2025-01-16 08:00'
  }, {
    time: '09:00',
    value: 49234.12,
    timestamp: '2025-01-16 09:00'
  }, {
    time: '10:00',
    value: 49356.34,
    timestamp: '2025-01-16 10:00'
  }, {
    time: '11:00',
    value: 49445.56,
    timestamp: '2025-01-16 11:00'
  }, {
    time: '12:00',
    value: 49523.78,
    timestamp: '2025-01-16 12:00'
  }, {
    time: '13:00',
    value: 49612.9,
    timestamp: '2025-01-16 13:00'
  }, {
    time: '14:00',
    value: 49734.12,
    timestamp: '2025-01-16 14:00'
  }, {
    time: '15:00',
    value: 49823.34,
    timestamp: '2025-01-16 15:00'
  }, {
    time: '16:00',
    value: 49890.56,
    timestamp: '2025-01-16 16:00'
  }, {
    time: '17:00',
    value: 49945.78,
    timestamp: '2025-01-16 17:00'
  }, {
    time: '18:00',
    value: 50012.9,
    timestamp: '2025-01-16 18:00'
  }, {
    time: '19:00',
    value: 50078.12,
    timestamp: '2025-01-16 19:00'
  }, {
    time: '20:00',
    value: 50123.34,
    timestamp: '2025-01-16 20:00'
  }, {
    time: '21:00',
    value: 50167.56,
    timestamp: '2025-01-16 21:00'
  }, {
    time: '22:00',
    value: 50201.78,
    timestamp: '2025-01-16 22:00'
  }, {
    time: '23:00',
    value: 50223.9,
    timestamp: '2025-01-16 23:00'
  }, {
    time: '24:00',
    value: 50234.67,
    timestamp: '2025-01-16 24:00'
  }],
  '7D': [{
    time: 'Mon',
    value: 46823.45,
    timestamp: '2025-01-13'
  }, {
    time: 'Tue',
    value: 47912.67,
    timestamp: '2025-01-14'
  }, {
    time: 'Wed',
    value: 47234.89,
    timestamp: '2025-01-15'
  }, {
    time: 'Thu',
    value: 48567.23,
    timestamp: '2025-01-16'
  }, {
    time: 'Fri',
    value: 49345.78,
    timestamp: '2025-01-17'
  }, {
    time: 'Sat',
    value: 48923.45,
    timestamp: '2025-01-18'
  }, {
    time: 'Sun',
    value: 50234.67,
    timestamp: '2025-01-19'
  }],
  '30D': [{
    time: 'Week 1',
    value: 44567.89,
    timestamp: '2024-12-20'
  }, {
    time: 'Week 2',
    value: 47234.56,
    timestamp: '2024-12-27'
  }, {
    time: 'Week 3',
    value: 46123.45,
    timestamp: '2025-01-03'
  }, {
    time: 'Week 4',
    value: 50234.67,
    timestamp: '2025-01-10'
  }],
  '1Y': [{
    time: 'Jan',
    value: 34567.23,
    timestamp: '2024-01'
  }, {
    time: 'Feb',
    value: 36890.45,
    timestamp: '2024-02'
  }, {
    time: 'Mar',
    value: 35678.12,
    timestamp: '2024-03'
  }, {
    time: 'Apr',
    value: 38234.67,
    timestamp: '2024-04'
  }, {
    time: 'May',
    value: 40123.89,
    timestamp: '2024-05'
  }, {
    time: 'Jun',
    value: 42567.34,
    timestamp: '2024-06'
  }, {
    time: 'Jul',
    value: 41234.56,
    timestamp: '2024-07'
  }, {
    time: 'Aug',
    value: 44890.23,
    timestamp: '2024-08'
  }, {
    time: 'Sep',
    value: 46567.78,
    timestamp: '2024-09'
  }, {
    time: 'Oct',
    value: 45123.45,
    timestamp: '2024-10'
  }, {
    time: 'Nov',
    value: 48456.89,
    timestamp: '2024-11'
  }, {
    time: 'Dec',
    value: 50234.67,
    timestamp: '2024-12'
  }],
  ALL: [{
    time: '2020',
    value: 15234.78,
    timestamp: '2020'
  }, {
    time: '2021',
    value: 25678.45,
    timestamp: '2021'
  }, {
    time: '2022',
    value: 30456.89,
    timestamp: '2022'
  }, {
    time: '2023',
    value: 38123.56,
    timestamp: '2023'
  }, {
    time: '2024',
    value: 45789.23,
    timestamp: '2024'
  }, {
    time: '2025',
    value: 50234.67,
    timestamp: '2025'
  }]
};
// Custom tooltip component
const CustomTooltip = ({
  active,
  payload
}: any) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  const value = payload[0].value;
  return <div style={{
    background: 'rgba(20, 22, 32, 0.65)',
    backdropFilter: 'blur(32px) saturate(180%)',
    WebkitBackdropFilter: 'blur(32px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '28px',
    padding: '16px 20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
  }} data-id="element-447">
      {/* Timestamp */}
      <div style={{
      color: '#9CA3AF',
      fontSize: '11px',
      marginBottom: '8px',
      letterSpacing: '0.05em',
      fontWeight: '400',
      textTransform: 'uppercase'
    }} data-id="element-448">
        {data.timestamp}
      </div>

      {/* Portfolio Value */}
      <div style={{
      color: '#FFFFFF',
      fontSize: '28px',
      fontWeight: '300',
      letterSpacing: '-0.02em'
    }} data-id="element-449">
        $
        {value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}
      </div>
    </div>;
};
export function PortfolioOverview() {
  const [timeRange, setTimeRange] = useState<keyof typeof chartData>('24H');
  const [activeAccountTab, setActiveAccountTab] = useState<'main' | 'futures'>('main');
  const ranges: Array<keyof typeof chartData> = ['24H', '7D', '30D', '1Y', 'ALL'];
  // Get data for selected time range
  const data = useMemo(() => {
    const rawData = chartData[timeRange];
    // Add previousValue for change calculation in tooltip
    return rawData.map((item, index) => ({
      ...item,
      previousValue: index > 0 ? rawData[index - 1].value : item.value
    }));
  }, [timeRange]);
  // Calculate change based on time range
  const changeData = useMemo(() => {
    if (data.length < 2) return {
      amount: 0,
      percent: 0
    };
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    const amount = lastValue - firstValue;
    const percent = amount / firstValue * 100;
    return {
      amount,
      percent
    };
  }, [data]);
  return <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 bg-background-secondary border border-border-subtle rounded-[20px] p-6" data-id="element-450">
      {/* Left: Chart & Value */}
      <div className="flex flex-col h-full" data-id="element-451">
        <div className="flex items-start justify-between mb-6" data-id="element-452">
          <div data-id="element-453">
            <div className="text-sm text-text-secondary mb-1 font-label uppercase" data-id="element-454">
              Total Portfolio Value
            </div>
            <div className="flex items-baseline gap-2 mb-1" data-id="element-455">
              <h1 className="text-4xl font-light text-text-primary tracking-tight font-numbers" data-id="element-456">
                {totalPortfolioValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </h1>
              <span className="text-lg text-text-secondary font-normal" data-id="element-457">
                USD
              </span>
            </div>
            <span className={`flex items-center gap-1 text-sm font-medium ${changeData.amount >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-458">
              <TrendingUp size={14} className={changeData.amount < 0 ? 'rotate-180' : ''} data-id="element-459" />
              {changeData.amount >= 0 ? '+' : ''}$
              {Math.abs(changeData.amount).toLocaleString()} (
              {changeData.percent >= 0 ? '+' : ''}
              {changeData.percent.toFixed(2)}%){' '}
              <span className="text-text-secondary" data-id="element-460">{timeRange}</span>
            </span>
          </div>

          <div className="flex items-center gap-2 bg-background-elevated p-1 rounded-lg" data-id="element-461">
            {ranges.map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-3 py-1 text-xs font-medium rounded-md transition-all border ${timeRange === range ? 'bg-background-elevated text-text-primary border-border-default' : 'text-text-secondary border-transparent hover:text-text-primary hover:bg-white/5'}`} data-id="element-462">
                {range}
              </button>)}
          </div>
        </div>

        {/* Chart - shown above account cards on mobile, on right side on desktop */}
        <div className="relative w-full max-w-[800px] h-[300px] mb-6 xl:hidden overflow-hidden mx-auto" data-id="element-463">
          <ResponsiveContainer width="100%" height="100%" data-id="element-464">
            <AreaChart data={data} margin={{
            left: 10,
            right: 10,
            top: 10,
            bottom: 10
          }} data-id="element-465">
              <defs data-id="element-466">
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-467">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-468" />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-469" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-470" />
              <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
              fill: '#848E9C',
              fontSize: 11
            }} dy={0} interval="preserveEnd" minTickGap={50} data-id="element-471" />
              <YAxis hide domain={['dataMin - 100', 'dataMax + 100']} data-id="element-472" />
              <Tooltip content={<CustomTooltip timeRange={timeRange} data-id="element-474" />} wrapperStyle={{
              zIndex: 1000
            }} cursor={{
              stroke: '#8B5CF6',
              strokeWidth: 1,
              strokeDasharray: '4 4'
            }} data-id="element-473" />
              <Area type="monotoneX" dataKey="value" stroke="#8B5CF6" strokeWidth={1.5} fillOpacity={1} fill="url(#colorValue)" animationDuration={1000} animationEasing="ease-in-out" data-id="element-475" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Grid layout: accounts on left, chart on right (desktop only) */}
        <div className="flex-1 grid grid-cols-1 xl:grid-cols-[minmax(340px,380px)_1fr] gap-6 min-h-[300px]" data-id="element-476">
          {/* Left: Account balances card + pill buttons below */}
          <div className="flex flex-col gap-3 relative z-10" data-id="element-477">
            {/* Account Tabs */}
            <div className="flex items-center gap-1 mb-1" data-id="element-478">
              <button onClick={() => setActiveAccountTab('main')} className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${activeAccountTab === 'main' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-479">
                Main
              </button>
              <button onClick={() => setActiveAccountTab('futures')} className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${activeAccountTab === 'futures' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-480">
                Futures
              </button>
            </div>

            {/* Account balances - increased border radius from rounded-2xl to rounded-[24px] */}
            <div className="bg-[rgba(104,107,130,0.12)] rounded-[24px] overflow-hidden p-2" data-id="element-481">
              {activeAccountTab === 'main' ? <div className="flex flex-col gap-2" data-id="element-482">
                  {/* Main Account - increased border radius */}
                  <button className="w-full p-4 hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] transition-colors text-left" data-id="element-483">
                    <div className="flex items-center justify-between mb-3" data-id="element-484">
                      <div className="text-sm font-heading text-text-primary" data-id="element-485">
                        Main Account
                      </div>
                      <div className="p-1.5 bg-brand-purple/10 rounded-lg" data-id="element-486">
                        <Coins size={14} className="text-brand-purple-light" data-id="element-487" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-488">
                      <div className="flex items-center justify-between text-xs" data-id="element-489">
                        <span className="text-text-secondary font-label" data-id="element-490">
                          Total value
                        </span>
                        <span className="font-numbers text-text-primary" data-id="element-491">
                          {mainAccountValue.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-492">USD</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-493">
                        <span className="text-text-secondary font-label" data-id="element-494">
                          Available margin
                        </span>
                        <span className="font-numbers text-text-primary" data-id="element-495">
                          {availableMargin.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-496">USD</span>
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Spot & Margin */}
                  <div className="grid grid-cols-2 gap-2" data-id="element-497">
                    {/* Spot Account - increased border radius, 2 decimals */}
                    <button className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-498">
                      <div className="text-xs font-heading text-text-primary mb-2" data-id="element-499">
                        Spot
                      </div>
                      <div className="space-y-1.5" data-id="element-500">
                        <div className="flex items-center justify-between" data-id="element-501">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-502">
                            Balance
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-503">
                            {spotBalance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-text-secondary ml-0.5" data-id="element-504">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-505">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-506">
                            Change (24h)
                          </div>
                          <div className={`font-numbers text-xs ${spotChange24h >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-507">
                            {spotChange24h >= 0 ? '+' : ''}
                            {spotChange24h.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-[10px] ml-0.5" data-id="element-508">
                              ({spotChange24h >= 0 ? '+' : ''}
                              {spotChange24hPercent.toFixed(2)}%)
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Margin Account - increased border radius, 2 decimals */}
                    <button className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-509">
                      <div className="flex items-center justify-between mb-2" data-id="element-510">
                        <div className="text-xs font-heading text-text-primary" data-id="element-511">
                          Margin
                        </div>
                        <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-512">
                          {currentLeverage.toFixed(1)}x
                        </div>
                      </div>
                      <div className="space-y-1.5" data-id="element-513">
                        <div className="flex items-center justify-between" data-id="element-514">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-515">
                            Balance
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-516">
                            {marginBalance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-text-secondary ml-0.5" data-id="element-517">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-518">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-519">
                            Used
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-520">
                            {marginUsedMargin.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Earn Account - increased border radius, higher balance, weekly rewards */}
                  <button className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-521">
                    <div className="text-xs font-heading text-text-primary mb-2" data-id="element-522">
                      Earn
                    </div>
                    <div className="space-y-1.5" data-id="element-523">
                      <div className="flex items-center justify-between" data-id="element-524">
                        <div className="text-[10px] text-text-secondary font-label" data-id="element-525">
                          Balance
                        </div>
                        <div className="font-numbers text-text-primary text-xs" data-id="element-526">
                          {earnBalance.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-527">USD</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-528">
                        <div className="text-[10px] text-text-secondary font-label" data-id="element-529">
                          Rewards (1w)
                        </div>
                        <div className="font-numbers text-success text-xs" data-id="element-530">
                          +{earnRewards.toFixed(2)}{' '}
                          <span className="text-text-secondary" data-id="element-531">USD</span>
                        </div>
                      </div>
                    </div>
                  </button>
                </div> : <div className="flex flex-col gap-2" data-id="element-532">
                  {/* Futures Account Card - increased border radius */}
                  <button className="w-full p-4 hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] transition-colors text-left" data-id="element-533">
                    <div className="flex items-center justify-between mb-3" data-id="element-534">
                      <div className="text-sm font-heading text-text-primary" data-id="element-535">
                        Futures Account
                      </div>
                      <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-536">
                        {futuresLeverage.toFixed(1)}x
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-537">
                      <div className="flex items-center justify-between text-xs" data-id="element-538">
                        <span className="text-text-secondary font-label" data-id="element-539">
                          Total Balance
                        </span>
                        <span className="font-numbers text-text-primary" data-id="element-540">
                          {futuresAccountValue.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-541">USD</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-542">
                        <span className="text-text-secondary font-label" data-id="element-543">
                          Unrealized P&L
                        </span>
                        <span className={`font-numbers ${futuresUnrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-544">
                          {futuresUnrealizedPnl >= 0 ? '+' : ''}
                          {futuresUnrealizedPnl.toFixed(2)}{' '}
                          <span className="text-text-secondary" data-id="element-545">USD</span>
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Isolated & Cross Margin */}
                  <div className="grid grid-cols-2 gap-2" data-id="element-546">
                    {/* Isolated Margin - increased border radius, 2 decimals */}
                    <button className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-547">
                      <div className="flex items-center justify-between mb-2" data-id="element-548">
                        <div className="text-xs font-heading text-text-primary" data-id="element-549">
                          Isolated
                        </div>
                        <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-550">
                          {isolatedLeverage.toFixed(1)}x
                        </div>
                      </div>
                      <div className="space-y-1.5" data-id="element-551">
                        <div className="flex items-center justify-between" data-id="element-552">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-553">
                            Balance
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-554">
                            {isolatedMarginBalance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-text-secondary ml-0.5" data-id="element-555">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-556">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-557">
                            UP&L
                          </div>
                          <div className={`font-numbers text-xs ${isolatedMarginUpnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-558">
                            {isolatedMarginUpnl >= 0 ? '+' : ''}
                            {isolatedMarginUpnl.toFixed(2)}
                            <span className="text-text-secondary ml-0.5" data-id="element-559">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-560">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-561">
                            Health
                          </div>
                          <div className={`font-numbers text-xs ${isolatedMarginHealth > 50 ? 'text-success' : isolatedMarginHealth > 25 ? 'text-warning' : 'text-danger'}`} data-id="element-562">
                            {isolatedMarginHealth.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Cross Margin - increased border radius, 2 decimals */}
                    <button className="w-full bg-[#686b821F] hover:bg-[rgba(104,107,130,0.18)] rounded-[20px] p-3 transition-colors text-left" data-id="element-563">
                      <div className="flex items-center justify-between mb-2" data-id="element-564">
                        <div className="text-xs font-heading text-text-primary" data-id="element-565">
                          Cross
                        </div>
                        <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-566">
                          {crossLeverage.toFixed(1)}x
                        </div>
                      </div>
                      <div className="space-y-1.5" data-id="element-567">
                        <div className="flex items-center justify-between" data-id="element-568">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-569">
                            Balance
                          </div>
                          <div className="font-numbers text-text-primary text-xs" data-id="element-570">
                            {crossMarginBalance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                            <span className="text-text-secondary ml-0.5" data-id="element-571">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-572">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-573">
                            UP&L
                          </div>
                          <div className={`font-numbers text-xs ${crossMarginUpnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-574">
                            {crossMarginUpnl >= 0 ? '+' : ''}
                            {crossMarginUpnl.toFixed(2)}
                            <span className="text-text-secondary ml-0.5" data-id="element-575">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-576">
                          <div className="text-[10px] text-text-secondary font-label" data-id="element-577">
                            Health
                          </div>
                          <div className={`font-numbers text-xs ${crossMarginHealth > 50 ? 'text-success' : crossMarginHealth > 25 ? 'text-warning' : 'text-danger'}`} data-id="element-578">
                            {crossMarginHealth.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>}
            </div>

            {/* Action buttons - pill shaped, below the card */}
            <div className="flex items-center gap-2" data-id="element-579">
              <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-580">
                <Download size={14} data-id="element-581" />
                Deposit
              </button>
              <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-582">
                <Upload size={14} data-id="element-583" />
                Withdraw
              </button>
              <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-584">
                <ArrowLeftRight size={14} data-id="element-585" />
                Transfer
              </button>
            </div>
          </div>

          {/* Right: Chart (desktop only) - Full width with right side fixed, left fades */}
          <div className="relative h-full hidden xl:block" data-id="element-586">
            <div className="absolute inset-0 overflow-hidden" data-id="element-587">
              <div className="absolute right-0 w-[1200px] min-w-full h-full" data-id="element-588">
                <ResponsiveContainer width="100%" height="100%" data-id="element-589">
                  <AreaChart data={data} margin={{
                  left: 0,
                  right: 16,
                  top: 0,
                  bottom: 0
                }} data-id="element-590">
                    <defs data-id="element-591">
                      <linearGradient id="colorValueDesktop" x1="0" y1="0" x2="0" y2="1" data-id="element-592">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} data-id="element-593" />
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} data-id="element-594" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2B3139" vertical={false} opacity={0.3} data-id="element-595" />
                    <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                    fill: '#848E9C',
                    fontSize: 11
                  }} dy={0} interval="preserveEnd" minTickGap={50} data-id="element-596" />
                    <YAxis hide domain={['dataMin - 100', 'dataMax + 100']} data-id="element-597" />
                    <Tooltip content={<CustomTooltip timeRange={timeRange} data-id="element-599" />} wrapperStyle={{
                    zIndex: 1000
                  }} cursor={{
                    stroke: '#8B5CF6',
                    strokeWidth: 1,
                    strokeDasharray: '4 4'
                  }} data-id="element-598" />
                    <Area type="monotoneX" dataKey="value" stroke="#8B5CF6" strokeWidth={1.5} fillOpacity={1} fill="url(#colorValueDesktop)" animationDuration={1000} animationEasing="ease-in-out" data-id="element-600" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Left fade overlay - fades chart on the left, keeps right visible */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-background-secondary via-background-secondary/80 to-transparent pointer-events-none z-10" data-id="element-601" />
          </div>
        </div>
      </div>

      {/* Right: Allocation */}
      <div className="hidden lg:block border-l border-border-subtle pl-6" data-id="element-602">
        <AllocationChart data-id="element-603" />
      </div>
    </div>;
}